import MarketTabSection from '../../../common/MarketTabSection';
import { useOrderDetails } from '../../OrderDetailsProvider';
import { fetchMembershipOrderDetailsPreviewOrderList } from '../../membership-order-details-mock';

import { CPLAT_API_ROUTES, fetchOrderChangeList, orderQueries, useFetch } from '@/api';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import GlobalSpinner from '@/components/organisms/GlobalSpinner';
import { DEFAULT_PAGE_SIZE } from '@/constants/order';
import { useGetMarketIdFromSeachParams } from '@/hooks/use-get-market-id-from-search-params';
import { useInfiniteOrderList } from '@/pages/orders/_hooks/useInfiniteOrderList';
import { LayoutStickySection, LayoutTables } from '@/pages/orders/_layouts/LayoutOrderContainer';
import LayoutTableController from '@/pages/orders/_layouts/LayoutTableController';
import { useAppSelector } from '@/redux/store';
import type { CplatApiResponse } from '@/types/api';
import type { OrderSeachField } from '@/types/order-list';
import type { Stores } from '@/types/stores';
import { formatISOString } from '@/utils/format-iso-string';
import { useState } from 'react';
import DateRangeSelect from '../../../common/DateRangeSelect';
import NotRegisteredState from '../../../common/NotRegisteredState';
import { SearchFilter } from '../../../common/SearchFilter';
import Controller from './Controller';
import FilterButton from './FilterButton';
import OrderTableColumn from './OrderTableColumn';
import { useSort } from '@/contexts/table-sort';

const Cancel = () => {
  const user = useAppSelector((state) => state.user);
  const isPreviewMode = useMembershipPreviewMode();

  const [searchField, setSearchField] = useState<OrderSeachField>('ExternalProductInfo');
  const [searchKeyword, setSearchKeyword] = useState('');

  const {
    // 날짜 관련 상태와 함수
    startDate,
    endDate,
    handleStartDate,
    handleEndDate,
  } = useOrderDetails();

  const { storeId, marketId } = useGetMarketIdFromSeachParams();

  const [isOrderFurtherActionEnableOnly, setIsOrderFurtherActionEnableOnly] = useState(false);
  const { sort } = useSort();

  const filterParams = {
    dateFrom: formatISOString(startDate),
    dateTo: formatISOString(endDate),
    ...(storeId && { storeId }),
    ...(marketId && { marketId: Number(marketId) }),
    ...(sort && {
      sortInfo: { sortColumn: sort.sortColumn, sortDirection: sort.sortDirection },
    }),
    ...(isOrderFurtherActionEnableOnly && {
      isOrderFurtherActionEnable: isOrderFurtherActionEnableOnly,
    }),
    ...(searchField &&
      searchKeyword && {
        searchField,
        searchKeyword,
      }),
  };

  const getOrderChangeListQuery = useInfiniteOrderList({
    queryKey: [
      ...orderQueries.all(),
      'getOrderChangeList',
      'infinite',
      'CancelRequest',
      filterParams,
    ],
    pageSize: DEFAULT_PAGE_SIZE,
    enabled: isPreviewMode || Boolean(user.CplatToken),
    queryFn: ({ skip, take }) =>
      isPreviewMode
        ? fetchMembershipOrderDetailsPreviewOrderList('CancelRequest', {
            ...filterParams,
            skip,
            take,
          })
        : fetchOrderChangeList({
            cplatToken: user.CplatToken,
            ...filterParams,
            skip,
            take,
            statusTab: 'CancelRequest',
          }),
  });

  const orderChangeListData = getOrderChangeListQuery.orderList;

  const { data: storeListData } = useFetch<CplatApiResponse<Stores[][]>>(
    CPLAT_API_ROUTES.getStores,
    {
      method: 'POST',
      params: { cplatToken: user.CplatToken },
      config: { enabled: !isPreviewMode },
      v3: true,
    },
  );

  const hasRegisteredStores = isPreviewMode || (storeListData?.data[0].length ?? 0) >= 1;

  if (getOrderChangeListQuery.isLoading) return <GlobalSpinner />;

  return (
    <>
      {hasRegisteredStores ? (
        <>
          <LayoutStickySection>
            <MarketTabSection
              totalLength={
                getOrderChangeListQuery.pagination?.totalCount ?? orderChangeListData.length
              }
            />
            <LayoutTableController>
              <div className="flex gap-[20px]">
                <DateRangeSelect
                  startDate={startDate}
                  handleStartDate={handleStartDate}
                  endDate={endDate}
                  handleEndDate={handleEndDate}
                />
                <SearchFilter
                  searchField={searchField}
                  searchKeyword={searchKeyword}
                  setSearchKeyword={setSearchKeyword}
                  setSearchField={setSearchField}
                  onSearch={(keyword) => setSearchKeyword(keyword)}
                  onReset={() => setSearchKeyword('')}
                />
                <FilterButton
                  canOrderCount={getOrderChangeListQuery.availableActionCount}
                  receiverFilter={isOrderFurtherActionEnableOnly}
                  setReceiverFilter={setIsOrderFurtherActionEnableOnly}
                  title={isPreviewMode ? '처리 가능한 건 보기' : '처리 가능한 건만 보기'}
                />
              </div>
              <Controller />
            </LayoutTableController>
          </LayoutStickySection>
          <LayoutTables isTableBorder>
            <OrderTableColumn
              orderList={orderChangeListData}
              listEndSentinelRef={getOrderChangeListQuery.listEndSentinelRef}
              hasNextPage={getOrderChangeListQuery.hasNextPage}
              isFetchingNextPage={getOrderChangeListQuery.isFetchingNextPage}
            />
          </LayoutTables>
        </>
      ) : (
        <NotRegisteredState className="bg-white" />
      )}
    </>
  );
};

export default Cancel;
